import {
  Conversation,
  ConversationStatus,
  ProcessingStep,
  ProcessingStepStatus,
  ProcessingStepType,
  TERMINAL_STEP_STATUSES,
} from "@/types";

/**
 * Returns true if the conversation has processing steps (feature flag is on).
 */
export function hasProcessingSteps(conversation?: Conversation): boolean {
  return Array.isArray(conversation?.processingSteps) && conversation.processingSteps.length > 0;
}

/**
 * Finds a specific processing step by type.
 */
export function getStepByType(
  conversation: Conversation | undefined,
  stepType: ProcessingStepType,
): ProcessingStep | undefined {
  return conversation?.processingSteps?.find((s) => s.stepType === stepType);
}

/**
 * Returns true when the page content should be shown.
 *
 * Conversation status is the source of truth for overall completion:
 *  - DONE / ERROR_SUMMARY → ready (page shell renders; summary tab handles
 *    its own inline error UI for ERROR_SUMMARY). Reprocessed conversations
 *    can land here with a partial processing-steps array (e.g. no
 *    SUGGESTED_PARTICIPANTS row), so we must not gate on specific step rows.
 *  - IN_PROGRESS / PROCESSING_* → fall back to step rows.
 *
 * In the live-processing fallback, SUMMARY must exist and be terminal.
 * SUGGESTED_PARTICIPANTS is only required if its row is present — that way
 * we wait for it on fresh conversations (so unassigned-speaker UI doesn't
 * flash) but don't block reprocessed conversations that never emit it.
 */
export function isPageReady(conversation?: Conversation): boolean {
  if (!conversation) return false;

  if (
    conversation.status === ConversationStatus.DONE ||
    conversation.status === ConversationStatus.ERROR_SUMMARY
  ) {
    return true;
  }

  if (!hasProcessingSteps(conversation)) return false;

  const summaryStep = getStepByType(conversation, ProcessingStepType.SUMMARY);
  if (!summaryStep || !TERMINAL_STEP_STATUSES.includes(summaryStep.status)) {
    return false;
  }

  const suggestedParticipantsStep = getStepByType(
    conversation,
    ProcessingStepType.SUGGESTED_PARTICIPANTS,
  );
  if (suggestedParticipantsStep) {
    return TERMINAL_STEP_STATUSES.includes(suggestedParticipantsStep.status);
  }

  return true;
}

/**
 * Returns true when the transcription step has failed.
 * Without processing steps (legacy): checks for ERROR_TRANSCRIPT status.
 */
export function isTranscriptionFailed(conversation?: Conversation): boolean {
  if (!conversation) return false;

  if (!hasProcessingSteps(conversation)) {
    return conversation.status === ConversationStatus.ERROR_TRANSCRIPT;
  }

  const transcriptionStep = getStepByType(conversation, ProcessingStepType.TRANSCRIPTION);
  return transcriptionStep?.status === ProcessingStepStatus.ERRORED;
}

/**
 * Returns the error reason from the transcription step, or null.
 */
export function getTranscriptionErrorReason(conversation?: Conversation): string | null {
  if (!hasProcessingSteps(conversation)) return null;

  const transcriptionStep = getStepByType(conversation, ProcessingStepType.TRANSCRIPTION);
  return transcriptionStep?.errorReason ?? null;
}

/**
 * Returns true for fatal errors that should show full-screen error.
 * With processing steps: only transcription ERRORED is fatal.
 * Without processing steps (legacy): ERROR_TRANSCRIPT and ERROR_UNKNOWN are fatal.
 * ERROR_SUMMARY is intentionally NOT fatal — it renders inline in the summary tab
 * so users can still see the transcript and reprocess.
 */
export function isFatalError(conversation?: Conversation): boolean {
  if (!conversation) return false;

  if (!hasProcessingSteps(conversation)) {
    return (
      conversation.status === ConversationStatus.ERROR_TRANSCRIPT ||
      conversation.status === ConversationStatus.ERROR_UNKNOWN
    );
  }

  return isTranscriptionFailed(conversation);
}

/**
 * Returns true when the summary step is WAITING or INPROGRESS.
 */
export function isSummaryLoading(conversation?: Conversation): boolean {
  if (!hasProcessingSteps(conversation)) return false;

  const summaryStep = getStepByType(conversation, ProcessingStepType.SUMMARY);
  return (
    summaryStep?.status === ProcessingStepStatus.WAITING ||
    summaryStep?.status === ProcessingStepStatus.INPROGRESS
  );
}

/**
 * Returns true when the summary should render as errored in the summary tab.
 * Either source is sufficient:
 *  - `conversation.status === ERROR_SUMMARY` (covers legacy/no-steps and
 *    cases where the conversation status flipped before the step row did).
 *  - The SUMMARY processing step is ERRORED.
 */
export function isSummaryErrored(conversation?: Conversation): boolean {
  if (!conversation) return false;

  if (conversation.status === ConversationStatus.ERROR_SUMMARY) return true;

  if (!hasProcessingSteps(conversation)) return false;

  const summaryStep = getStepByType(conversation, ProcessingStepType.SUMMARY);
  return summaryStep?.status === ProcessingStepStatus.ERRORED;
}

/**
 * Returns the error reason from the summary step, or null.
 */
export function getSummaryErrorReason(conversation?: Conversation): string | null {
  if (!hasProcessingSteps(conversation)) return null;

  const summaryStep = getStepByType(conversation, ProcessingStepType.SUMMARY);
  return summaryStep?.errorReason ?? null;
}

/**
 * Returns true when the coaching step is ERRORED.
 * NOT_APPLICABLE coaching steps return false (the tab should be hidden, not show an error).
 */
export function isCoachingStepErrored(conversation?: Conversation): boolean {
  if (!hasProcessingSteps(conversation)) return false;

  const coachingStep = getStepByType(conversation, ProcessingStepType.COACHING);
  return coachingStep?.status === ProcessingStepStatus.ERRORED;
}

/**
 * Returns the error reason from the coaching step, or null.
 */
export function getCoachingStepErrorReason(conversation?: Conversation): string | null {
  if (!hasProcessingSteps(conversation)) return null;

  const coachingStep = getStepByType(conversation, ProcessingStepType.COACHING);
  return coachingStep?.errorReason ?? null;
}

/**
 * Returns true when the data extraction step is ERRORED.
 * NOT_APPLICABLE data extraction steps return false (the tab should be hidden, not show an error).
 */
export function isDataExtractionStepErrored(conversation?: Conversation): boolean {
  if (!hasProcessingSteps(conversation)) return false;

  const dataExtractionStep = getStepByType(conversation, ProcessingStepType.DATA_EXTRACTION);
  return dataExtractionStep?.status === ProcessingStepStatus.ERRORED;
}

/**
 * Returns the error reason from the data extraction step, or null.
 */
export function getDataExtractionStepErrorReason(conversation?: Conversation): string | null {
  if (!hasProcessingSteps(conversation)) return null;

  const dataExtractionStep = getStepByType(conversation, ProcessingStepType.DATA_EXTRACTION);
  return dataExtractionStep?.errorReason ?? null;
}

/**
 * Returns true if any processing step is in a non-terminal state (for polling logic).
 */
export function isAnyStepProcessing(conversation?: Conversation): boolean {
  if (!hasProcessingSteps(conversation)) return false;

  return conversation!.processingSteps!.some(
    (step) => !TERMINAL_STEP_STATUSES.includes(step.status),
  );
}
